import type { TranslationMessages } from '../types';

export const settings: TranslationMessages = {
  name: 'Settings',
  title: 'Settings',
  modernOnly: 'This settings page is only available in the modern version.',
  currency: {
    title: 'Currency Settings',
    subtitle:
      'Configure how currency is displayed throughout the application',
    presets: 'Common Currencies',
    custom: 'Custom Settings',
    symbol: 'Currency Symbol',
    symbolHelp: 'The symbol to display (e.g., $, €, ₫)',
    code: 'Currency Code',
    codeHelp: 'The three-letter currency code (e.g., USD, EUR, VND)',
    precision: 'Decimal Places',
    precisionHelp: 'Number of decimal places to display (0-4)',
    position: 'Symbol Position',
    positionBefore: 'Before amount ($100)',
    positionAfter: 'After amount (100₫)',
    thousandsSeparator: 'Thousands Separator',
    thousandsSeparatorHelp:
      'Character to separate thousands (e.g., , or .)',
    decimalSeparator: 'Decimal Separator',
    decimalSeparatorHelp: 'Character to separate decimals (e.g., . or ,)',
    reset: 'Reset to Default',
    preview: 'Preview',
    previewAmount: 'Amount to preview',
    previewDescription:
      'See how your currency settings will look in the application',
  },
  notifications: {
    title: 'Notification Settings',
    subtitle: 'Configure notification preferences for your organization',
    reset: 'Reset to Default',
    info: 'Configure when notifications are sent based on inventory stock levels. Thresholds must be in order: Critical < Low < Medium.',
    global: 'Global Settings',
    enabled: 'Enable all notifications',
    enabledHelp:
      'Master switch to enable or disable all notifications. Individual categories can still be controlled below.',
    inventoryStock: 'Inventory Stock Notifications',
    inventoryStockEnabled: 'Enable inventory stock notifications',
    inventoryStockEnabledHelp:
      'Receive alerts when inventory levels fall below configured thresholds.',
    thresholds: 'Stock Level Thresholds',
    criticalThreshold: 'Critical Threshold',
    criticalThresholdHelp:
      'Alert when stock ≤ this level (CRITICAL priority)',
    lowThreshold: 'Low Threshold',
    lowThresholdHelp: 'Alert when stock ≤ this level (HIGH priority)',
    mediumThreshold: 'Medium Threshold',
    mediumThresholdHelp: 'Reference threshold (not used for notifications)',
    thresholdOrderWarning:
      'Thresholds must be in order: Critical < Low < Medium',
    howItWorks: 'How It Works',
    howItWorksDescription:
      'When inventory quantity falls below the configured thresholds, notifications will be sent to store managers and organization admins. Notifications are sent via real-time channels and appear in the notification center.',
    orderNotifications: 'Order Notifications',
    orderNotificationsEnabled: 'Enable all order notifications',
    orderNotificationsEnabledHelp:
      'Master switch for all order-related notifications.',
    orderNew: 'New Order Notifications',
    orderNewHelp: 'Notify when a new order is placed',
    orderReady: 'Order Ready Notifications',
    orderReadyHelp: 'Notify when an order is marked as ready',
    orderDelivered: 'Order Delivered Notifications',
    orderDeliveredHelp: 'Notify when an order is delivered',
    orderCancelled: 'Order Cancelled Notifications',
    orderCancelledHelp: 'Notify when an order is cancelled',
    staffNotifications: 'Staff Notifications',
    staffNotificationsEnabled: 'Enable staff notifications',
    staffNotificationsEnabledHelp:
      'Receive notifications about staff activities, attendance, and shift changes.',
    generalNotifications: 'General Notifications',
    generalNotificationsEnabled: 'Enable general notifications',
    generalNotificationsEnabledHelp:
      'Receive general system notifications and announcements.',
  },
  staff: {
    title: 'Staff Settings',
    fetch_failed: 'Failed to load staff settings',
    save_success: 'Staff settings updated successfully',
    save_failed: 'Failed to update staff settings',
    paid_leave_title: 'Paid Leave Settings',
    paid_leave_desc:
      'Configure how paid days off are allocated and managed for staff',
    allowance_label: 'Annual Paid Day Off Allowance',
    allowance_hint: 'Number of paid days off staff receive each year',
    wipe_policy_label: 'End of Year Policy',
    wipe_policy_hint:
      'Specify if unused days are carries over or reset at year end',
    wipe_option: 'Wipe (Reset to zero)',
    keep_option: 'Keep (Carry over to next year)',
    fiscal_start_label: 'Fiscal Year Start Month',
    fiscal_start_hint: 'The month when the leave allowance resets',
    standard_hours_label: 'Standard Weekly Hours',
    standard_hours_hint:
      'Target working hours per week for full-time staff',
    effective_policy: 'Effective Staff Policy',
    currently_using: 'Currently using',
    rules: 'rules',
    paid_time_off: 'Paid Time Off:',
    weekly_load: 'Weekly Load:',
    daily_load: 'Daily Load:',
    configuration_hierarchy: 'Configuration Hierarchy',
    hierarchy_description:
      'Settings are applied in order of priority: Weekly → Monthly → Yearly. Enabling a higher priority setting will override lower ones.',
    weekly_settings: 'Weekly Settings',
    enable_weekly_rules: 'Enable Weekly Rules',
    specific_days_off: 'Specific Days Off',
    days_off_count_per_week: 'Or Days Off Count (Per Week)',
    standard_weekly_hours: 'Standard Weekly Hours',
    standard_daily_hours: 'Standard Daily Hours',
    monthly_settings: 'Monthly Settings',
    enable_monthly_rules: 'Enable Monthly Rules',
    days_off_per_month: 'Days Off per Month',
    days_per_year: 'days/year',
    hours_per_week: 'hours/week',
    hours_per_day: 'hours/day',
    policies: 'Policies',
    annual_paid_day_off_allowance: 'Annual Paid Day Off Allowance',
    annual_allowance_hint: 'Total days per year. Used if no Weekly/Monthly rules apply.',
    fiscal_year_start_month: 'Fiscal Year Start Month',
    reset_policy: 'Reset Policy',
    wipe_monthly: 'Wipe Monthly (Reset every month)',
    wipe_yearly: 'Wipe Yearly (Reset at fiscal year end)',
    keep_accumulate: 'Keep (Accumulate indefinitely)',
    highest_priority: 'Highest Priority',
    overrides_policies: 'Overrides Policies',
    days_of_week: {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday',
    },
  },
  sensitiveData: {
    title: 'Sensitive Data Protection',
    description:
      'Configure which fields should be blurred when taking screenshots, reporting bugs, or sending screenshots to the AI assistant',
    noPermission:
      'You do not have permission to access sensitive data settings. Only administrators and store managers can access this page.',
    info: 'Blurring is automatically activated when you take a screenshot, report a bug, or send a screenshot to the AI assistant. Use the preview button to test your configuration.',
    globalSettings: 'Global Settings',
    globalSettingsDescription:
      'Enable or disable sensitive data blurring globally',
    enableBlurring: 'Enable Sensitive Data Blurring',
    enableBlurringDescription:
      'When enabled, sensitive data will be automatically blurred during screenshots, bug reports, and AI assistant interactions',
    blurIntensity: 'Blur Intensity',
    blurIntensityDescription:
      'Higher values create stronger blur. Recommended: 8',
    fieldConfiguration: 'Field Configuration',
    fieldConfigurationDescription:
      'Configure which fields should be blurred. Fields are automatically detected based on CSS selectors and data attributes.',
    preview: 'Preview',
    reset: 'Reset',
    save: 'Save',
    enabled: 'Sensitive data blurring enabled',
    disabled: 'Sensitive data blurring disabled',
    fieldEnabled: 'Enabled blurring for field',
    fieldDisabled: 'Disabled blurring for field',
    previewStopped: 'Preview stopped',
    previewStoppedAndClosed: 'Preview stopped and tab closed',
    previewWindowOpened:
      'Preview window opened - blurring is active in the new tab',
    allowPopups: 'Please allow popups to open preview window',
    resetToDefault: 'Reset to default configuration',
    settingsSaved: 'Settings saved successfully',
    maximum: 'Maximum',
    categories: {
      personal: 'Personal Information',
      financial: 'Financial Information',
      contact: 'Contact Information',
      identification: 'Identification',
      other: 'Other',
    },
    table: {
      field: 'Field',
      description: 'Description',
      selector: 'Selector',
      enabled: 'Enabled',
    },
  },
  payment: {
    title: 'Payment Settings',
    subtitle: 'Configure payment QR code for receipts',
    reset: 'Reset',
    info: 'Configure a payment QR code that will be included in printed receipts. You can either upload an existing QR code image or enter payment information to generate one automatically.',
    uploadQRImage: 'Upload QR Code Image',
    uploadQRImageHelp:
      'Upload an existing QR code image. If you upload an image, the text data below will be ignored.',
    removeImage: 'Remove',
    invalidImageType: 'Please upload an image file',
    imageTooLarge: 'Image size must be less than 2MB',
    imageUploadError: 'Failed to read image file',
    or: 'or',
    generateAutomatically: 'generate automatically',
    qrCodeData: 'QR Code Data',
    qrCodeDataPlaceholder:
      'Enter payment information (e.g., bank account, e-wallet, payment link)',
    qrCodeDataHelp:
      'This data will be encoded into the QR code. Customers can scan it to make payments. Leave empty if you uploaded an image above.',
    qrCodeLabel: 'QR Code Label',
    qrCodeLabelPlaceholder: 'Scan to Pay',
    qrCodeLabelHelp:
      'Optional label displayed above the QR code on receipts',
    includeInReceipt: 'Include in Receipt',
    includeInReceiptHelp: 'Show QR code in the footer of printed receipts',
    preview: 'Preview',
    hidePreview: 'Hide',
    showPreview: 'Show',
    previewError:
      'Unable to generate QR code preview. Please check your QR code data.',
  },
  fiscalMonth: {
    title: 'Fiscal Month Settings',
    subtitle: 'Configure custom fiscal month periods for reporting',
    info: "Fiscal months allow you to define custom monthly periods that don't align with calendar months. For example, a fiscal month starting on the 15th runs from the 15th of one month to the 14th of the next month.",
    enable: 'Enable Fiscal Months',
    enableDescription:
      'Use custom fiscal month periods instead of calendar months',
    reset: 'Reset to Default',
    configuration: 'Configuration',
    startDay: 'Fiscal Month Start Day',
    startDayHelp: 'Day of the month when each fiscal month begins (1-31)',
    presets: 'Quick Presets',
    calendarMonth: 'Calendar (1st)',
    midMonth: 'Mid-Month (15th)',
    endMonth: 'End-Month (25th)',
    preview: 'Preview',
    previewDate: 'Preview Date',
    fiscalPeriod: 'Fiscal Period',
    start: 'Start',
    end: 'End',
    previewDescription:
      'This shows the fiscal month period that contains the selected date',
    disabledMessage:
      'Fiscal months are currently disabled. Enable them above to configure custom monthly periods.',
  },
  };
